import { htmlSafe } from '@ember/template';
import { not } from 'tio-ui/utilities';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { Section } from 'tio-reimagined/components/layout';
import AppContentCard from 'tio-common/components/app/content-card';
import Button from 'tio-reimagined/components/button';
import Component from '@glimmer/component';
import { dropTask } from 'ember-concurrency';
import inc from 'tio-common/helpers/inc';
import { PhPrinter } from 'ember-phosphor-icons';
import screenIs from 'tio-common/helpers/screen-is';
import Table from 'tio-reimagined/components/table';
import DownloadButton from 'tio-reimagined/components/download-button';
import type Store from '@ember-data/store';
import type RecommendationModel from 'tio-data/models/recommendation';

export interface ForgivenessEstimatorResultInstructionsSignature {
  Args: {
    recommendation: RecommendationModel;
  };
}

export default class ForgivenessEstimatorResultInstructionsComponent extends Component<ForgivenessEstimatorResultInstructionsSignature> {
  @service declare store: Store;

  get instructions() {
    return this.args.recommendation?.nextSteps || [];
  }

  downloadAndOpenInNewTab = dropTask(async (url: string) => {
    if (!url) {
      return;
    }
    const adapter = this.store.adapterFor('application');
    const headers = adapter.headers;
    const response = await fetch(url, { headers });
    if (response.ok) {
      const blob = await response.blob();
      const fileUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = fileUrl;
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noreferrer noopener');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      console.error('Unable to download file due to an error.', response);
    }
  });

  <template>
    {{#if (screenIs "md")}}
      <Section>
        <:header>
          <div class="tio-flex-end text-sm gap-x-4">
            <Button
              @appearance="outlined"
              @intent="primary"
              @isRunning={{this.downloadAndOpenInNewTab.isRunning}}
              {{on "click" (fn this.downloadAndOpenInNewTab.perform @recommendation.downloadUrl)}}
            >
              <PhPrinter @size="16" class="mr-2" />
              {{t "common.print"}}
            </Button>
            <DownloadButton
              @url={{@recommendation.downloadUrl}}
              @filename={{@recommendation.downloadFilename}}
            />
          </div>
        </:header>
        <:body>
          <Table>
            <:thead as |options|>
              <options.tr>
                {{#each this.instructions as |column|}}
                  <options.th>
                    {{column.title}}
                    {{#if column.subtitle}}
                      <div class="font-normal">
                        {{column.subtitle}}
                      </div>
                    {{/if}}
                  </options.th>
                {{/each}}
              </options.tr>
            </:thead>
            <:tbody as |options|>
              <options.tr>
                {{#each this.instructions as |row|}}
                  <options.td class="align-top">
                    {{#if row.subtitle}}
                      <p class="font-semibold mx-2 mt-4 min-h-12">{{row.subtitle}}:</p>
                    {{/if}}
                    <ul class="flex-col top-10">
                      {{#each row.instructions as |step index|}}
                        <li class="flex my-4 mx-2">
                          <div class="rounded-full mr-2 w-6 h-6 tio-row-center shrink-0">{{inc
                              index
                            }}</div>
                          <span class="step-by-step-table-links">
                            {{htmlSafe step}}
                          </span>
                        </li>
                      {{/each}}
                    </ul>
                  </options.td>
                {{/each}}
              </options.tr>
            </:tbody>
          </Table>
        </:body>
      </Section>
    {{else}}
      {{#each this.instructions as |card|}}
        <AppContentCard @title={{card.title}} class="m-4 even:bg-ocean-50">
          {{#if card.subtitle}}
            <h2 class="italicized">{{card.subtitle}}:</h2>
          {{/if}}
          <ul class="flex-col">
            {{#each card.instructions as |step index|}}
              <li class="flex my-4 mx-2">
                <div class="rounded-full mr-2 w-6 h-6 tio-row-center shrink-0">{{inc index}}</div>
                <span class="step-by-step-table-links">
                  {{htmlSafe step}}
                </span>
              </li>
            {{/each}}
          </ul>
        </AppContentCard>
      {{/each}}

      {{#if (not (screenIs "md"))}}
        <div class="flex w-full items-center justify-around">
          <Button
            @appearance="outlined"
            @intent="primary"
            @isRunning={{this.downloadAndOpenInNewTab.isRunning}}
            {{on "click" (fn this.downloadAndOpenInNewTab.perform @recommendation.downloadUrl)}}
            class="border-none"
          >
            <PhPrinter @size="16" class="mr-2" />
            {{t "common.print"}}
          </Button>
          <DownloadButton
            @url={{@recommendation.downloadUrl}}
            @filename={{@recommendation.downloadFilename}}
            class="border-none"
          />
        </div>
      {{/if}}
    {{/if}}
  </template>
}
